import type { PaidPlanPricingResult } from './subscriptionPaidPlanPricing';

const PENDING_PASS_CHECKOUT_KEY = 'curriculum-pending-pass-checkout.v1';
export const POST_LOGIN_REDIRECT_KEY = 'curriculum-post-login-redirect';

type SerializedPricing = {
  purchaseDate: string;
  periodStart: string;
  periodEndInclusive: string;
  billedPeriodEndInclusive: string;
  lines: PaidPlanPricingResult['lines'];
  totalWon: number;
  billingAnchorFirstDay?: string;
};

export type PendingPassGroupForm = {
  groupName: string;
  email: string;
  quantity: number;
  agreeActivation: boolean;
  agreeRefund: boolean;
  billingStartIso: string;
};

export type PendingPassCheckout =
  | { v: 1; action: 'confirmIndividual'; pricing: SerializedPricing }
  | {
      v: 1;
      action: 'confirmGroup';
      basePricing: SerializedPricing;
      groupForm: PendingPassGroupForm;
    }
  | {
      v: 1;
      action: 'submitCoupon';
      couponModalKind: 'group' | 'event' | 'beta';
      couponCodeInput: string;
    };

function serializePricing(p: PaidPlanPricingResult): SerializedPricing {
  return {
    purchaseDate: p.purchaseDate.toISOString(),
    periodStart: p.periodStart.toISOString(),
    periodEndInclusive: p.periodEndInclusive.toISOString(),
    billedPeriodEndInclusive: p.billedPeriodEndInclusive.toISOString(),
    lines: p.lines,
    totalWon: p.totalWon,
    ...(p.billingAnchorFirstDay
      ? { billingAnchorFirstDay: p.billingAnchorFirstDay.toISOString() }
      : {}),
  };
}

export function revivePricing(raw: SerializedPricing): PaidPlanPricingResult {
  return {
    purchaseDate: new Date(raw.purchaseDate),
    periodStart: new Date(raw.periodStart),
    periodEndInclusive: new Date(raw.periodEndInclusive),
    billedPeriodEndInclusive: new Date(raw.billedPeriodEndInclusive),
    lines: Array.isArray(raw.lines) ? raw.lines : [],
    totalWon: Number(raw.totalWon) || 0,
    ...(raw.billingAnchorFirstDay
      ? { billingAnchorFirstDay: new Date(raw.billingAnchorFirstDay) }
      : {}),
  };
}

export function savePendingIndividualCheckout(pricing: PaidPlanPricingResult): void {
  writePending({ v: 1, action: 'confirmIndividual', pricing: serializePricing(pricing) });
}

export function savePendingGroupCheckout(
  basePricing: PaidPlanPricingResult,
  groupForm: PendingPassGroupForm,
): void {
  writePending({ v: 1, action: 'confirmGroup', basePricing: serializePricing(basePricing), groupForm });
}

export function savePendingCouponCheckout(
  couponModalKind: 'group' | 'event' | 'beta',
  couponCodeInput: string,
): void {
  writePending({ v: 1, action: 'submitCoupon', couponModalKind, couponCodeInput });
}

function writePending(payload: PendingPassCheckout): void {
  try {
    sessionStorage.setItem(PENDING_PASS_CHECKOUT_KEY, JSON.stringify(payload));
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/subscription');
  } catch {
    /* ignore */
  }
}

export function peekPendingPassCheckout(): PendingPassCheckout | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PASS_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPassCheckout;
    if (!parsed || parsed.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function takePendingPassCheckout(): PendingPassCheckout | null {
  const pending = peekPendingPassCheckout();
  try {
    sessionStorage.removeItem(PENDING_PASS_CHECKOUT_KEY);
  } catch {
    /* ignore */
  }
  return pending;
}

export function takePostLoginRedirect(): string | null {
  try {
    const path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    return path && path.startsWith('/') ? path : null;
  } catch {
    return null;
  }
}

/** 랜딩 /pass 등에서 로그인만 한 경우에도 이용권 페이지로 */
export function markPostLoginPassRedirect(): void {
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/subscription');
  } catch {
    /* ignore */
  }
}
